import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  type?: "website" | "article" | "product";
  jsonLd?: object | object[];
  noindex?: boolean;
}

const DEFAULT_TITLE = "Altar Stone | Countertop Installation Sarasota FL";
const DEFAULT_DESC = "Premium quartz, marble, quartzite & granite countertops in Sarasota and Southwest Florida. Free online quote. Call (941) 541-3348.";
const SITE_URL = "https://www.countertopsaltarstone.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

function setMeta(attr: "name" | "property", key: string, value: string) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(data: object | object[] | undefined) {
  // Remove previous dynamic JSON-LD
  document.head.querySelectorAll('script[data-seo="dynamic"]').forEach((n) => n.remove());
  if (!data) return;
  const arr = Array.isArray(data) ? data : [data];
  arr.forEach((d) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-seo", "dynamic");
    s.text = JSON.stringify(d);
    document.head.appendChild(s);
  });
}

export function useSEO({ title, description, canonical, image, type = "website", jsonLd, noindex }: SEOProps) {
  useEffect(() => {
    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    const url = canonical
      ? canonical.startsWith("http")
        ? canonical
        : `${SITE_URL}${canonical.startsWith("/") ? canonical : `/${canonical}`}`
      : typeof window !== "undefined"
      ? window.location.origin + window.location.pathname
      : SITE_URL;

    setLink("canonical", url);

    // Open Graph
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type === "product" ? "product" : type);
    setMeta("property", "og:url", url);
    if (image) setMeta("property", "og:image", image);

    // Twitter
    setMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    if (image) setMeta("name", "twitter:image", image);

    setJsonLd(jsonLd);

    return () => {
      document.title = DEFAULT_TITLE;
      setMeta("name", "description", DEFAULT_DESC);
      setMeta("name", "robots", "index, follow");
      // Reset Open Graph + Twitter to site-wide defaults so stale per-page
      // metadata doesn't leak into social previews on the next route.
      setMeta("property", "og:title", DEFAULT_TITLE);
      setMeta("property", "og:description", DEFAULT_DESC);
      setMeta("property", "og:type", "website");
      setMeta("property", "og:url", SITE_URL);
      setMeta("property", "og:image", DEFAULT_OG_IMAGE);
      setMeta("name", "twitter:card", "summary_large_image");
      setMeta("name", "twitter:title", DEFAULT_TITLE);
      setMeta("name", "twitter:description", DEFAULT_DESC);
      setMeta("name", "twitter:image", DEFAULT_OG_IMAGE);
      setLink("canonical", SITE_URL);
      setJsonLd(undefined);
    };
  }, [title, description, canonical, image, type, JSON.stringify(jsonLd), noindex]);
}
